<template>
  <div
    class="elements"
    :class="{ horizontal, timeRelation }"
    :data-parent-relation="parent.childRel"
    :data-task-id="parent._id"
    :data-child-count="visibleElems.length"
  >
    <component
      v-for="(elem, idx) in visibleElems"
      :is="compType"
      :parent="parent"
      :itemID="elem._id"
      :key="elem._id"
      :data-parent-relation="parent.childRel"
      :data-task-id="elem._id"
      :data-idx="idx"
    />
  </div>
</template>

<style scoped>
.elements {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 8px;
}
.elements.horizontal {
  flex-direction: row;
}

.hidden-info {
  font-size: var(--text-size-secondary);
  opacity: var(--text-opacity-secondary);
  text-align: left;
  padding: 0 10px;
  font-style: italic;
}
</style>

<script>
import { REL_ELEM, REL_PLAN } from "@/structure";
import selection from "../selection";
import OmicronDnd from "../omicron-dnd.js";
import _ from "lodash";
import { getItem } from "../reactive-cache";
import { insertItemsAfter } from "@/ordered-relation";
import { Item } from "@/item";
import { insertItems, appendItems } from "@/relations-adapter";

export default {
  name: "SortableList",
  props: ["parent", "elements", "compType", "horizontal"],
  components: {
    // Async import to solve circular dependency and make recursion work. See
    // https://v2.vuejs.org/v2/guide/components-edge-cases.html#Circular-References-Between-Components
    card: () => import("./ItemCard.vue"),
    list: () => import("./ItemList.vue"),
  },
  data() {
    return {
      beforeUpdateActions: [],
    };
  },
  computed: {
    elemCount() {
      return this.elements.length;
    },
    hiddenCount() {
      return this.elemCount - this.visibleElems.length;
    },
    visibleElems() {
      return this.elements.filter((el) => el.isVisible());
    },
    timeRelation() {
      return this.parent.childRel === REL_PLAN;
    },
  },
  methods: {
    insertBefore(elem) {
      selection.insertBefore(elem, this.parent);
    },
    append() {
      selection.appendTo(this.parent);
    },
    reorder(movedTaskElement, placementDataset, parentPlacement) {
      const draggedTask = getItem(movedTaskElement.dataset.taskId);
      if (parentPlacement) {
        const parent = getItem(placementDataset.taskId);
        if (placementDataset.childCount === "0") {
          const updated = appendItems(parent, [draggedTask]);
          Item.saveAll(updated);
        } else {
          const updated = insertItems(parent, [draggedTask], 0);
          Item.saveAll(updated);
        }
      } else {
        const putAfterTask = getItem(placementDataset.taskId);
        const parent = putAfterTask.getParent(placementDataset.parentRelation);
        const updated = insertItemsAfter(putAfterTask, parent, [draggedTask]);
        Item.saveAll(updated);
      }
    },
    handleSortableReorder(event) {
      // `event.item` is the moved element, and it is already inserted in the
      // new place, so that event.item.previousElementSibling can be used to
      // get the preceding task/section. This may be complicated by some
      // special elements getting in the way, so we attempt to skip them.
      // In case we are at the very top pf the container, we may find nothing,
      // in which case we will take the data from the container.
      let putAfterElement = event.item.previousElementSibling;
      // Skip elements without the right data set on them.
      while (
        putAfterElement &&
        !putAfterElement.dataset.placementInfo &&
        !putAfterElement.dataset.taskId
      ) {
        putAfterElement = putAfterElement.previousElementSibling;
      }
      const placementDataset = (putAfterElement || event.item.parentElement).dataset;
      const parentPlacement = !putAfterElement;

      if (!placementDataset.placementInfo && !placementDataset.taskId) {
        // There is no suitable placementDataset for this drop spot.
        // This might happen at the top of the list-view (at least, it
        // happened historically). Simply revert the drag, as we can't
        // meaningfully implement it.
        console.warn("No placement info found for reorder event:", event);
        this.revertSortableReorder(event);
        return;
      }
      // if (this.dragWouldCreateCycle(event.to, event.item)) {
      //   console.warn(
      //     "Cannot drop an item inside itself:",
      //     this.$taskCardsController.selectedTasksIds,
      //     event,
      //   );
      //   this.revertSortableReorder(event);
      //   return;
      // }

      // if (this.teamMismatch(event.to, event.item, placementDataset.parentRelation)) {
      //   console.warn(
      //     "Mismatching teams:",
      //     this.$taskCardsController.selectedTasksIds,
      //     event,
      //   );
      //   alert(this.$gettext("Target belongs to a different team."));
      //   this.revertSortableReorder(event);
      //   return;
      // }

      // Dragging the element made actual DOM inconsistent with Vue's virtual
      // DOM. This can result in Vue making DOM update incorrectly. To prevent
      // that, we revert the drag. However, if we reverted the move
      // immediately, the element would be visibly moved back and then again
      // to the new place. To prevent this flickering, we defer the revert
      // action to just before Vue updates DOM in one of the parent elements.
      //
      // Before it's hard to tell which tasks-list will get updated first,
      // I schedule triggers on both, and the first one will trigger reverting
      // the dom change.
      const revertFunc = _.once(() => {
        this.revertSortableReorder(event);
      });
      event.item.classList.add("has-revert-action");
      event.item.revertAction = revertFunc;
      this.beforeUpdateActions.push(revertFunc);

      this.reorder(event.item, placementDataset, parentPlacement);
    },
    revertSortableReorder(event) {
      // There's a weird edge case when the inserted element is placed
      // after the ghost/clone element created by Sortable, and then after
      // the clone is removed, event.newIndex starts pointing beyond the
      // end of the event.to.children list.
      const realNewIndex = Math.min(event.newIndex, event.to.children.length - 1);
      if (event.from === event.to && event.newIndex <= event.oldIndex) {
        event.from.insertBefore(
          event.to.children[realNewIndex],
          event.from.children[event.oldIndex].nextSibling,
        );
      } else {
        event.from.insertBefore(
          event.to.children[realNewIndex],
          event.from.children[event.oldIndex],
        );
      }
    },
  },
  mounted() {
    OmicronDnd.init(this.$el, {
      draggableSelector: ".draggable",
      filterSelector: ".no-drag",
      onInternalChange: (event) => {
        this.handleSortableReorder(event);
      },
      onDropToOtherContainer: (event) => {
        this.handleSortableReorder(event);
      },
    });
  },
  beforeUpdate() {
    for (const action of this.beforeUpdateActions) {
      action();
    }
    this.beforeUpdateActions = [];

    const els = this.$el.querySelectorAll(".has-revert-action");
    els.forEach((el) => {
      el.revertAction();
      delete el.revertAction;
      el.classList.remove("has-revert-action");
    });
  },
};
</script>
